/**
 * Système de couleurs pour les badges des matières
 * Assigne des couleurs cohérentes basées sur le nom de la matière
 */

export const SUBJECT_COLORS = {
  // Sciences exactes
  mathematiques: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800/40',
  },
  physique: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800/40',
  },
  chimie: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-400',
    border: 'border-cyan-200 dark:border-cyan-800/40',
  },
  svt: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800/40',
  },
  biologie: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800/40',
  },
  // Langues
  francais: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800/40',
  },
  anglais: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-700 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-800/40',
  },
  espagnol: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-400',
    border: 'border-yellow-200 dark:border-yellow-800/40',
  },
  allemand: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800/40',
  },
  // Sciences humaines
  histoire: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800/40',
  },
  geographie: {
    bg: 'bg-teal-100 dark:bg-teal-900/30',
    text: 'text-teal-700 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-800/40',
  },
  philosophie: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    text: 'text-violet-700 dark:text-violet-400',
    border: 'border-violet-200 dark:border-violet-800/40',
  },
  // Autres
  eps: {
    bg: 'bg-lime-100 dark:bg-lime-900/30',
    text: 'text-lime-700 dark:text-lime-400',
    border: 'border-lime-200 dark:border-lime-800/40',
  },
  arts: {
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    text: 'text-pink-700 dark:text-pink-400',
    border: 'border-pink-200 dark:border-pink-800/40',
  },
  musique: {
    bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30',
    text: 'text-fuchsia-700 dark:text-fuchsia-400',
    border: 'border-fuchsia-200 dark:border-fuchsia-800/40',
  },
  informatique: {
    bg: 'bg-slate-100 dark:bg-slate-900/30',
    text: 'text-slate-700 dark:text-slate-400',
    border: 'border-slate-200 dark:border-slate-800/40',
  },
  technologie: {
    bg: 'bg-zinc-100 dark:bg-zinc-900/30',
    text: 'text-zinc-700 dark:text-zinc-400',
    border: 'border-zinc-200 dark:border-zinc-800/40',
  },
} as const;

const DEFAULT_COLOR = {
  bg: 'bg-gray-100 dark:bg-gray-900/30',
  text: 'text-gray-700 dark:text-gray-400',
  border: 'border-gray-200 dark:border-gray-800/40',
};

/**
 * Obtient la couleur d'un badge basée sur le nom de la matière
 */
export function getSubjectColor(subjectName: string) {
  const normalized = subjectName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Supprime les accents

  // Cherche une correspondance exacte ou partielle
  for (const [key, color] of Object.entries(SUBJECT_COLORS)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return color;
    }
  }

  return DEFAULT_COLOR;
}

/** Clé pour tags SCSS school (`tagBlue`, `tagPurple`, …) d’après mots-clés matière. */
export type SubjectKeywordTagKey =
  | 'blue'
  | 'purple'
  | 'teal'
  | 'green'
  | 'magenta';

/**
 * Mot-clés dans libellé ou code matière (math, fran, svt, …) → pastille carte.
 * Même règles que les anciennes fonctions locales Lesson/Chapter/TeachingCard.
 */
export function subjectKeywordTagKey(text: string): SubjectKeywordTagKey | '' {
  if (!text) return '';
  const t = text.toLowerCase();
  if (t.includes('math') || t.includes('phys')) return 'blue';
  if (t.includes('fran') || t.includes('hist')) return 'purple';
  if (t.includes('svt') || t.includes('bio')) return 'teal';
  if (t.includes('sport') || t.includes('eps')) return 'green';
  if (t.includes('art') || t.includes('musi')) return 'magenta';
  return '';
}

/** Associe la clé mot-clé aux classes SCSS du module (tagBlue, …). */
export function subjectKeywordScssClass(
  text: string,
  classes: Record<SubjectKeywordTagKey, string>
): string {
  const key = subjectKeywordTagKey(text);
  if (!key) return '';
  return classes[key];
}

/**
 * Génère un hash cohérent à partir d'une string pour assigner une couleur
 */
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Assigne une couleur cohérente basée sur un hash du nom
 * Utile pour les matières non prédéfinies
 */
export function getConsistentSubjectColor(subjectName: string) {
  // Essaie d'abord la correspondance prédéfinie
  const predefined = getSubjectColor(subjectName);
  if (predefined !== DEFAULT_COLOR) {
    return predefined;
  }

  // Sinon, utilise un hash pour assigner une couleur cohérente
  const colors = Object.values(SUBJECT_COLORS);
  const index = hashCode(subjectName) % colors.length;
  return colors[index];
}

/**
 * Palette de couleurs pour les classes/enseignements
 * Permet de différencier visuellement les différentes classes
 */
const CLASS_COLORS = [
  {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800/40',
  },
  {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800/40',
  },
  {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800/40',
  },
  {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800/40',
  },
  {
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    text: 'text-pink-700 dark:text-pink-400',
    border: 'border-pink-200 dark:border-pink-800/40',
  },
  {
    bg: 'bg-teal-100 dark:bg-teal-900/30',
    text: 'text-teal-700 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-800/40',
  },
  {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-700 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-800/40',
  },
  {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800/40',
  },
  {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-400',
    border: 'border-cyan-200 dark:border-cyan-800/40',
  },
  {
    bg: 'bg-rose-100 dark:bg-rose-900/30',
    text: 'text-rose-700 dark:text-rose-400',
    border: 'border-rose-200 dark:border-rose-800/40',
  },
] as const;

/**
 * Obtient une couleur cohérente pour une classe/enseignement
 * Basé sur le nom de la classe pour garantir la même couleur à chaque fois
 */
export function getConsistentClassColor(className: string) {
  const index = hashCode(className) % CLASS_COLORS.length;
  return CLASS_COLORS[index];
}

/** Palette hex pour codes matière (badges, pastilles) — stable par code. */
const SUBJECT_CODE_HEX_PALETTE = [
  '#3b82f6',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#f97316',
] as const;

/**
 * Couleur d’accent hex dérivée du code matière (ex. code national).
 * Même algorithme que les écrans profil historiques pour garder les couleurs.
 */
export function getSubjectCodeAccentHex(code: string): string {
  if (!code) return '#3b82f6';
  let hash = 0;
  for (let i = 0; i < code.length; i++) {
    hash = code.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % SUBJECT_CODE_HEX_PALETTE.length;
  return SUBJECT_CODE_HEX_PALETTE[idx] ?? '#3b82f6';
}
